import React, { useState, useEffect, useContext } from 'react';
import { Calendar, DateRangePicker, RangeKeyDict } from 'react-date-range';
import { ka, enUS, ru } from 'date-fns/locale';

import FormatData from '../../utils/FormatData';
import AppContext from '../../contexts/AppContext';
import { UseTranslate } from '../../hooks/Translate';

export type Props = {
  type: String;
  onConfirm: (range: RangeKeyDict | Date) => void;
  onLocaleChange: (range: RangeKeyDict) => void;
  selectionRange: RangeKeyDict | Date;
  cancelTranslationKeyword?: string;
  submitTranslationKeyword?: string;
  showIcon?: Boolean;
  placeholder?: string;
};

export default function CasCalendar(props: Props) {
  const [value, setValue] = useState('');
  const [visible, setVisible] = useState(false);
  const [date, setDate] = useState(new Date());
  const [translate] = UseTranslate();
  const [locale, setLocale] = useState(ka);
  const { selectedLanguage } = useContext(AppContext);
  const [langKey, setLangKey] = useState<string>();

  const [selectionRange, setSelectionRange] = useState(props.selectionRange);

  const [cancelTranslationKeyword, setCancelTranslationKeyword] = useState(
    'cas.calendar.cancel'
  );

  const [submitTranslationKeyword, setSubmitTranslationKeyword] = useState(
    'cas.calendar.submit'
  );

  useEffect(() => {
    if (selectedLanguage && selectedLanguage.key !== langKey) {
      setLangKey(selectedLanguage.key);
      switch (selectedLanguage.key) {
        case 'ru':
          setLocale(ru);
          break;
        case 'eng':
          setLocale(enUS);
          break;
        default:
          setLocale(ka);
      }

      window.setTimeout(function () {
        if (!(selectionRange instanceof Date)) {
          props.onLocaleChange(props.selectionRange as RangeKeyDict);
        }

      });
    }
  }, [props, selectionRange, selectedLanguage, langKey]);

  useEffect(() => {
    setSelectionRange(props.selectionRange);

    if (props.cancelTranslationKeyword) {
      setCancelTranslationKeyword(props.cancelTranslationKeyword);
    }

    if (props.submitTranslationKeyword) {
      setSubmitTranslationKeyword(props.submitTranslationKeyword);
    }

    if (props.type === 'DateRangePicker') {
      updateValue(props.selectionRange);
    } else {
      updateDateValue(props.selectionRange);
    }
  }, [props, selectedLanguage]);

  function onFocus() {
    setVisible(true);
    window.setTimeout(function () {
      document.addEventListener('click', clickHandler);
    }, 100);
  }

  function closeAndRemoveListener() {
    document.removeEventListener('click', clickHandler);
    setVisible(false);
  }

  function handleCancel() {
    setSelectionRange(selectionRange);
    closeAndRemoveListener();
  }

  function handleConfirm() {
    if (props.type === 'DateRangePicker') {
      updateValue(selectionRange);
    } else {
      updateDateValue(selectionRange);
    }
    props.onConfirm(selectionRange);
    setSelectionRange(selectionRange);
    closeAndRemoveListener();
  }

  function clickHandler(e: MouseEvent) {
    let parentNode = e.target as HTMLElement;

    while (parentNode) {
      if (/cas-calendar-wrapper/.test(parentNode.className)) return;
      if (/cas-calendar-backdrop/.test(parentNode.className)) break;
      parentNode = parentNode.parentNode as HTMLElement;
    }

    handleCancel();
  }

  function updateValue({ startDate, endDate }: any) {
    if (startDate && endDate) {
      const _endDate = FormatData.formatDate(endDate);
      const _startDate = FormatData.formatDate(startDate);
      setValue(`${_startDate} - ${_endDate}`);
    } else if (endDate) {
      setValue(FormatData.formatDate(endDate));
    } else if (startDate) {
      setValue(FormatData.formatDate(startDate));
    } else {
      setValue(String());
    }
  }

  function updateDateValue(date: any) {
    if (date && date instanceof Date) {
      setValue(FormatData.formatDate(date.toString()));
    }
  }

  function handleSelect(range: RangeKeyDict) {
    setSelectionRange((range as any).range1);
  }

  function handleDateSelect(date: any) {
    setDate(date);
    setSelectionRange(date);
  }

  return (
    <>
      <div
        className={`cas-calendar-wrapper ${visible ? 'visible' : ''} ${props.type === 'Calendar' ? 'cas-calendar-wrapper-override' : ''
          }`}
      >
        <div className="cas-calendar-backdrop"></div>
        <div className="rdrWrapper">
          {props.type === 'DateRangePicker' ? (
            <DateRangePicker
              color="#007A3E"
              ranges={[selectionRange as any]}
              onChange={handleSelect}
              locale={locale}
              startDatePlaceholder={translate('cas.translations.fromPeriod')}
              endDatePlaceholder={translate('cas.translations.toPeriod')}
              editableDateInputs={true}
            />
          ) : (
            <Calendar
              // @ts-ignore because of locale error
              color="#007A3E"
              locale={locale}
              date={date}
              onChange={handleDateSelect}
              maxDate={new Date()}
            />
          )}

          <div className="cas-calendar-buttons">
            <button type="button" className="btn" onClick={handleCancel}>
              {translate(cancelTranslationKeyword)}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleConfirm}
            >
              {translate(submitTranslationKeyword)}
            </button>
          </div>
        </div>
        <div className={`form-group ${props.showIcon ? 'show-icon' : ''}`}>
          <input
            type="text"
            className="form-control"
            placeholder={props.placeholder || ''}
            value={value}
            onFocus={onFocus}
            readOnly
          />
        </div>
      </div>
    </>
  );
}
